import React from 'react';
import cx from 'classnames';
import _startCase from 'lodash/startCase';
import { InstructionComponentType } from '@scale/shared/defaults';
import Markdown from '@scale/frontend-lib/markdown';
import { makeStyles } from 'design-system';
import Section from '../typeComponents/section';
import { SearchResultItemProps } from '../types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Sample from '../typeComponents/sample';
import { iconPropsByType, RICH_TEXT_TYPE_NAME } from '../constants';
import { useProjectInstructionContext } from '../projectInstructionContext';

const SearchPage = () => {
  const classes = useStyles();
  const { isMobileView, searchResult, setSearchResult, setSelectedPath } =
    useProjectInstructionContext();
  const highlight = (
    item: any,
    matches: { indices: Array<[number, number]>; key: string; value: string }[],
  ) => {
    const result: any = {};
    const { path } = item;
    const type = path[0];
    const displayName = type === InstructionComponentType.RichText ? RICH_TEXT_TYPE_NAME : type;
    const typeElement = (
      <>
        <FontAwesomeIcon
          icon={iconPropsByType[type]?.iconName}
          color={iconPropsByType[type]?.iconColor}
        />{' '}
        {_startCase(displayName)}
      </>
    );
    matches.map((match: { indices: Array<[number, number]>; key: string; value: string }) => {
      const { indices, key, value } = match;

      if (key === 'title') {
        let innerHTML: any = value;
        for (const [start, end] of indices) {
          innerHTML = (
            <span>
              {value.substring(0, start)}
              <span className={classes.highlight}>{value.substring(start, end + 1)}</span>
              {value.substring(end + 1)}
            </span>
          );
        }
        result[key] = (
          <>
            {typeElement} {'>'} {innerHTML}
          </>
        );
      } else if (key === 'description') {
        let innerHTML: string = value;

        const highlightHTMLs: Array<{ html: HTMLElement; size: number }> = [];
        for (const [start, end] of indices) {
          const highlightHTML = document.createElement('span');
          highlightHTML.style.cssText = 'background:yellow';
          highlightHTML.setAttribute('style', 'background:yellow;');
          highlightHTML.innerText = value.substring(start, end + 1);

          highlightHTMLs.push({ html: highlightHTML, size: end + 1 - start });
        }

        let offset = 0;
        indices.forEach(([start, end]: [number, number], index: number) => {
          const { html, size } = highlightHTMLs[index];

          const outerHTML = html.outerHTML;
          innerHTML =
            innerHTML.substring(0, start + offset) +
            outerHTML +
            innerHTML.substring(end + offset + 1);

          offset += outerHTML.length - size;
        });

        result[key] = innerHTML;
      }
    });

    if (!result.title) {
      result.title = (
        <>
          {typeElement} {'>'} {path[1]}
        </>
      );
    }

    if (!result.description) {
      result.description = item.description;
    }
    return result;
  };
  const num = searchResult?.length ?? 0;
  return (
    <div className={isMobileView ? classes.sidePanelContainer : classes.container}>
      <h1>
        Search Results ({num} result{num <= 1 ? '' : 's'})
      </h1>
      {!num && (
        <div className={classes.noResultContainer}>
          <div className={classes.circle}>
            <FontAwesomeIcon icon="search" size="2x" />
          </div>
          <div>No search results</div>
        </div>
      )}
      {searchResult?.map(
        ({ item, matches }: { item: SearchResultItemProps; matches: any }, index) => {
          const { title, description } = highlight(item, matches);
          const { path, linkedTask, nodeId } = item;

          const clickableTitle = (
            <span
              onClick={() => {
                setSelectedPath([path[0], nodeId]);
                setSearchResult(null);
              }}
              className={cx(classes.clickable, {
                [classes.smallerSize]: isMobileView,
              })}
            >
              {title}
            </span>
          );

          return (
            <Section key={index} sectionName={clickableTitle}>
              {linkedTask ? (
                <Sample sample={{ ...item, description }} index={index} />
              ) : description ? (
                <small
                  dangerouslySetInnerHTML={{
                    __html: Markdown.render(description || ''),
                  }}
                />
              ) : (
                <small>No Description</small>
              )}
            </Section>
          );
        },
      )}
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  container: {
    border: `1px solid ${theme.colors.gray10}`,
    background: 'white',
    padding: '10px 20px',
    borderRadius: 10,
    color: theme.colors.gray75,
    flex: 3,
    marginLeft: 20,
  },
  sidePanelContainer: {
    height: '100%',
    overflow: 'scroll',
    '& h1': {
      fontSize: 15,
      marginLeft: 10,
    },
    paddingBottom: 100,
  },
  highlight: {
    background: 'yellow',
  },
  clickable: {
    color: theme.colors.blue,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '&:hover': {
      cursor: 'pointer',
      textDecoration: 'underline',
    },
    '& svg': {
      marginRight: 6,
    },
  },

  smallerSize: {
    fontSize: 10,
  },
  circle: {
    height: 100,
    width: 100,
    borderRadius: '50%',
    background: theme.colors.gray10,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 10,
  },

  noResultContainer: {
    height: 700,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

export default SearchPage;
